/**
 * Public `/agent/change#token=…` — the Undo, Approve and Reject links in the
 * AI agent change emails (migration 096). No sign-in: the signed token in the
 * link, sent to one owner or admin, is the authority, and the server checks
 * they are still an owner or admin.
 *
 * The token lives in the URL FRAGMENT, so it never reaches a server log or a
 * Referer. The page only previews on load; acting is a button the person
 * presses, because mail scanners open links and must not undo or approve
 * anything.
 */
import { useEffect, useState } from 'react';
import type { AgentChangeLinkPreview, PendingChange } from '../api/types';
import { useSession } from '../auth/session';
import { KIND_WORDS } from '../agent/AgentChangesBanner';
import { Link } from '../router';
import { ErrorNotice, Loading, formatDateTime } from '../ui/kit';
import { ApiError } from '../api/errors';
import { reportTokenFromHash } from './SignupReport';

const TITLES: Record<AgentChangeLinkPreview['action'], string> = {
  undo: 'Undo an AI agent’s change',
  approve: 'Approve an AI agent’s request',
  reject: 'Reject an AI agent’s request',
};

const BUTTONS: Record<AgentChangeLinkPreview['action'], string> = {
  undo: 'Undo this change',
  approve: 'Approve',
  reject: 'Reject',
};

const DONE: Record<AgentChangeLinkPreview['action'], string> = {
  undo: 'Done. The change is undone.',
  approve: 'Approved. The change was made.',
  reject: 'Rejected. Nothing was changed.',
};

/** Why the button is not offered, in words. */
function whyNot(action: AgentChangeLinkPreview['action'], c: PendingChange): string {
  if (c.status === 'reverted') return `This change was already undone${c.reverted_at ? ` (${formatDateTime(c.reverted_at)})` : ''}.`;
  if (c.status === 'approved') return 'This request was already approved.';
  if (c.status === 'rejected') return 'This request was already rejected. Nothing was changed.';
  if (c.status === 'expired') return 'This request expired without a decision. Nothing was changed.';
  if (c.status === 'failed') return 'This change could not be made, so there is nothing to do.';
  if (action === 'undo') return 'The Undo link has expired. You can still change it back by hand in the portal.';
  return 'There is nothing to decide here any more.';
}

export function AgentChangeLink() {
  const { api } = useSession();
  const [token] = useState(() => reportTokenFromHash(window.location.hash));
  const [preview, setPreview] = useState<AgentChangeLinkPreview | null>(null);
  const [previewError, setPreviewError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [done, setDone] = useState<AgentChangeLinkPreview['action'] | null>(null);
  const [acting, setActing] = useState(false);
  const [actError, setActError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .previewAgentChangeLink(token)
      .then((p) => !cancelled && setPreview(p))
      .catch((err: unknown) => !cancelled && setPreviewError(err instanceof ApiError ? err : null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, token]);

  const action = preview?.action ?? 'undo';
  let body: React.ReactNode;
  if (!token) {
    body = (
      <div className="notice bad" role="alert">
        <span>This link is incomplete. Open the link from the email again, or sign in and use the top of your dashboard.</span>
      </div>
    );
  } else if (loading) {
    body = <Loading label="Checking the link" />;
  } else if (done) {
    body = (
      <div className="notice ok" role="status" data-testid="agent-change-done">
        <div className="stack-sm" style={{ gap: 4 }}>
          <strong>{DONE[done]}</strong>
          <span>
            <Link to="/dashboard">Open your dashboard</Link>
          </span>
        </div>
      </div>
    );
  } else if (previewError || !preview) {
    body = <ErrorNotice error={previewError ?? new ApiError({ status: 404, code: 'AGENT_CHANGE_LINK_INVALID' })} />;
  } else {
    const c = preview.change;
    body = (
      <div className="stack-sm">
        <p>
          {action === 'undo'
            ? 'An AI agent made this change for your organisation. It is live now.'
            : 'An AI agent asked for this change for your organisation. Nothing has happened yet.'}
        </p>
        <dl className="facts" data-testid="agent-change-facts">
          <dt>Organisation</dt>
          <dd>{preview.partner_name ?? '—'}</dd>
          <dt>What</dt>
          <dd style={{ overflowWrap: 'anywhere' }}>
            {KIND_WORDS[c.kind] ?? c.kind}: {c.summary}
          </dd>
          {c.changes?.length ? (
            <>
              <dt>Before → after</dt>
              <dd>
                <ul className="small agent-change-diff">
                  {c.changes.slice(0, 12).map((d, i) => (
                    <li key={`${d.field}-${i}`}>
                      <strong>{d.field}:</strong> {d.before} → {d.after}
                    </li>
                  ))}
                </ul>
              </dd>
            </>
          ) : null}
          <dt>Who</dt>
          <dd>
            The AI agent “{c.requested_by.agent_token_label ?? 'unknown'}”, acting for {c.requested_by.email ?? 'an unknown person'}
          </dd>
          <dt>When</dt>
          <dd>{formatDateTime(c.decided_at ?? c.created_at)}</dd>
          {action === 'undo' && c.undo?.until ? (
            <>
              <dt>Undo works until</dt>
              <dd>{formatDateTime(c.undo.until)}</dd>
            </>
          ) : null}
          {action !== 'undo' ? (
            <>
              <dt>Would apply</dt>
              <dd>
                {c.applies_at
                  ? `${formatDateTime(c.applies_at)}, unless someone rejects it`
                  : `Only when an owner or admin approves it (the request expires ${formatDateTime(c.expires_at)})`}
              </dd>
            </>
          ) : null}
          <dt>You</dt>
          <dd style={{ overflowWrap: 'anywhere' }}>{preview.signed_in_as}</dd>
        </dl>
        {c.note ? (
          <p className="small agent-quote">
            <span className="visually-hidden">The agent’s note: </span>“{c.note}”
          </p>
        ) : null}
        {preview.can_act ? (
          <>
            {action === 'undo' && c.undo?.how ? <p className="small">Undo will {c.undo.how.replace(/\.$/, '')}.</p> : null}
            <ErrorNotice error={actError} />
            <div className="row">
              <button
                type="button"
                className={`btn ${action === 'approve' ? 'btn-primary' : 'btn-danger'}`}
                disabled={acting}
                onClick={async () => {
                  setActing(true);
                  setActError(null);
                  try {
                    await api.actAgentChangeLink(token);
                    setDone(action);
                  } catch (err) {
                    setActError(err instanceof ApiError ? err : new ApiError({ status: 500, code: 'INTERNAL_ERROR' }));
                  } finally {
                    setActing(false);
                  }
                }}
              >
                {acting ? 'Working…' : BUTTONS[action]}
              </button>
            </div>
          </>
        ) : (
          <div className="notice info" role="status" data-testid="agent-change-closed">
            <span>{whyNot(action, c)}</span>
          </div>
        )}
        <p className="small muted">
          You can also do this at the top of your <Link to="/dashboard">dashboard</Link> after signing in.
        </p>
      </div>
    );
  }

  return (
    <main className="shared-dashboard legal-page" id="main">
      <header className="row-between legal-head">
        <Link to="/" className="eyebrow legal-home">
          FinderlyFix Partners
        </Link>
      </header>
      <article className="stack" aria-labelledby="agent-change-title">
        <h1 id="agent-change-title">{preview ? TITLES[preview.action] : 'AI agent change'}</h1>
        {body}
      </article>
    </main>
  );
}
